/**
 * BilanDetailPage - Detail view of an accounting statement
 *
 * Displays full details of a bilan with categories and export options.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBilanStore } from '../store/bilanStore';
import { useAccountStore } from '../store/accountStore';
import {
  BilanCategoryTable,
  BilanFormModal,
  BilanComparisonSelector,
} from '../components/bilans';
import { UpdateAccountingStatementRequest } from '@budgira/shared';
import { cn } from '../utils/cn';

const BilanDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const {
    currentStatement,
    statements,
    isLoadingDetail,
    isSubmitting,
    error,
    fetchStatement,
    fetchStatements,
    updateStatement,
    deleteStatement,
    recalculateStatement,
    exportPdf,
    clearCurrentStatement,
    clearError,
  } = useBilanStore();

  const { accounts, fetchAccounts } = useAccountStore();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);

  useEffect(() => {
    if (id) {
      fetchStatement(id);
      fetchStatements();
      fetchAccounts();
    }

    return () => {
      clearCurrentStatement();
    };
  }, [id, fetchStatement, fetchStatements, fetchAccounts, clearCurrentStatement]);

  const handleUpdate = async (data: UpdateAccountingStatementRequest) => {
    if (!id) return;
    const result = await updateStatement(id, data);
    if (result) {
      setIsEditOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const success = await deleteStatement(id);
    if (success) {
      navigate('/bilans');
    }
  };

  const handleExportPdf = async () => {
    if (!id) return;
    const blob = await exportPdf(id);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bilan_${currentStatement?.name || 'export'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleCompare = (id2: string) => {
    navigate(`/bilans/compare?id1=${id}&id2=${id2}`);
  };

  const handleRecalculate = async () => {
    if (!id) return;
    setIsRecalculating(true);
    await recalculateStatement(id);
    setIsRecalculating(false);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const activeAccounts = accounts.filter((a) => !a.isArchived);

  if (isLoadingDetail) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentStatement) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">{t('bilans.notFound')}</p>
        <Link
          to="/bilans"
          className="mt-4 inline-block text-violet-600 hover:text-violet-700"
        >
          ← {t('common.back')}
        </Link>
      </div>
    );
  }

  const isPositive = currentStatement.netResult >= 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link
            to="/bilans"
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 mb-2 inline-block"
          >
            ← {t('bilans.backToList')}
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {currentStatement.name}
          </h1>
          {currentStatement.description && (
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {currentStatement.description}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BilanComparisonSelector
            statements={statements}
            excludeId={id}
            onSelect={handleCompare}
          />
          <button
            onClick={handleRecalculate}
            disabled={isRecalculating}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshIcon className={cn('w-4 h-4', isRecalculating && 'animate-spin')} />
            {t('bilans.recalculate')}
          </button>
          <button
            onClick={handleExportPdf}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded-lg transition-colors"
          >
            <DownloadIcon className="w-4 h-4" />
            {t('bilans.exportPdf')}
          </button>
          <button
            onClick={() => setIsEditOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
          >
            <EditIcon className="w-4 h-4" />
            {t('common.edit')}
          </button>
          <button
            onClick={() => setIsDeleteOpen(true)}
            className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button onClick={clearError} className="text-red-500 hover:text-red-700">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Info card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('bilans.period')}</p>
            <p className="font-medium text-slate-900 dark:text-slate-100">
              {formatDate(currentStatement.startDate)} - {formatDate(currentStatement.endDate)}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('bilans.accounts')}</p>
            <p className="font-medium text-slate-900 dark:text-slate-100">
              {currentStatement.accounts.map((a) => a.accountName).join(', ')}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('bilans.createdAt')}</p>
            <p className="font-medium text-slate-900 dark:text-slate-100">
              {formatDate(currentStatement.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {/* Categories tables */}
      <div className="grid gap-6 lg:grid-cols-2">
        <BilanCategoryTable
          categories={currentStatement.categories}
          type="debit"
          total={currentStatement.totalExpenses}
          title={t('bilans.expenses')}
        />
        <BilanCategoryTable
          categories={currentStatement.categories}
          type="credit"
          total={currentStatement.totalIncome}
          title={t('bilans.income')}
        />
      </div>

      {/* Net result */}
      <div
        className={cn(
          'p-6 rounded-xl border-2 text-center',
          isPositive
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        )}
      >
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
          {t('bilans.netResult')}
        </p>
        <p
          className={cn(
            'text-3xl font-bold',
            isPositive
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400'
          )}
        >
          {isPositive ? '+' : ''}
          {formatAmount(currentStatement.netResult)}
        </p>
        <p className="text-sm mt-2 text-slate-500 dark:text-slate-400">
          {isPositive ? t('bilans.surplus') : t('bilans.deficit')}
        </p>
      </div>

      {/* Edit Modal */}
      <BilanFormModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSubmit={handleUpdate}
        statement={currentStatement}
        accounts={activeAccounts}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsDeleteOpen(false)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('bilans.deleteConfirmTitle')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t('bilans.deleteConfirmMessage')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DownloadIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const RefreshIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const EditIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default BilanDetailPage;
